/**
 * Builds the initial LegendaryGameState (G) from a validated MatchSetupConfig.
 *
 * Called by Game.setup() after validation succeeds. Resolves config fields
 * into zone arrays of CardExtId strings and constructs the full initial
 * game state.
 *
 * After this function returns, the engine operates solely on G and ctx.
 * No further registry access occurs at runtime.
 */
// why: WP-513 / D-24325 — value import for the Killbots per-scheme twist counter key.
import { KILLBOT_TWISTS_NEXT_TO_SCHEME } from '../types.js';
import { TURN_STAGES } from '../turn/turnPhases.types.js';
import { HAND_SIZE, drawCardsIntoHand } from '../moves/drawCards.logic.js';
import { buildPlayerState } from './playerInit.js';
import { buildGlobalPiles, BYSTANDER_EXT_ID, WOUND_EXT_ID, SHIELD_OFFICER_EXT_ID, SHIELD_AGENT_EXT_ID, SHIELD_TROOPER_EXT_ID, SIDEKICK_EXT_ID, } from './pilesInit.js';
import { resolveEffectiveWoundsCount, resolveEffectiveHeroDeckIds } from './schemeSetupSizing.js';
import { resolveEffectiveVillainGroupIds, splitMonsterPitFromVillainDeck, } from '../rules/clashMonsterPit.logic.js';
import { applyBrainwashedOfficerDisplay, countBrainwashedOfficers, } from '../rules/brainwashTheMilitary.logic.js';
import { buildDefaultHookDefinitions } from '../rules/ruleRuntime.impl.js';
import { resolveSchemeLossPileSetupSize, resolveSchemeLossVillainDeckSetupSize, } from '../rules/schemeLossProgress.js';
import { buildVillainDeck, isVillainDeckRegistryReader, } from '../villainDeck/villainDeck.setup.js';
import { initializeCity, fillHqFromDeck } from '../board/city.logic.js';
import { buildCardStats, resetTurnEconomy } from '../economy/economy.logic.js';
import { buildHeroDeck, buildTransformSideDeck, buildTransformTargets, buildSplitFaces, buildSplitFaceAlternateOnLeft } from './buildHeroDeck.js';
import { convertHeroesToSkrulls } from './convertHeroesToSkrulls.js';
import { buildMastermindState, isMastermindRegistryReader, } from '../mastermind/mastermind.setup.js';
import { buildHeroAbilityHooks, isHeroAbilityRegistryReader, } from './heroAbility.setup.js';
import { buildVillainAbilityHooks } from './villainAbility.setup.js';
import { buildVillainDefeatRequirements, buildVillainFightDiscardCosts, buildVillainVariableAttack, } from './villainDefeatRequirement.setup.js';
import { buildVillainBloodFrenzy } from './buildVillainBloodFrenzy.js';
import { buildCardAbomination } from './buildCardAbomination.js';
import { buildCardKeywords } from './buildCardKeywords.js';
import { buildCardTraits } from './buildCardTraits.js';
import { buildCardVictoryPoints } from './buildCardVictoryPoints.js';
import { buildSchemeSetupInstructions, isSchemeRegistryReader, } from './buildSchemeSetupInstructions.js';
import { applyKillbotDisplayNames, buildCardDisplayData, isCardDisplayDataRegistryReader, } from './buildCardDisplayData.js';
import { executeSchemeSetup } from '../scheme/schemeSetup.execute.js';
// why: WP-670 / D-24484 — Scheme Transform. For a scheme in this allowlist, setup captures
// its Great Old One target + that face's ability text into optional SchemeState fields.
import { SCHEME_TRANSFORM_TARGETS } from '../scheme/schemeTransform.logic.js';
// why: Pile + starting-card ext_id constants are re-exported from pilesInit.ts for
// backward compatibility. The canonical definitions live in pilesInit.ts — importing
// them here prevents silent drift from duplicate string literals. SHIELD_AGENT_EXT_ID
// / SHIELD_TROOPER_EXT_ID relocated to pilesInit under D-24296 (well-known basic
// S.H.I.E.L.D. ext_ids belong beside SHIELD_OFFICER_EXT_ID); existing importers of
// these names from this module are unaffected by the re-export.
export { BYSTANDER_EXT_ID, WOUND_EXT_ID, SHIELD_OFFICER_EXT_ID, SHIELD_AGENT_EXT_ID, SHIELD_TROOPER_EXT_ID, SIDEKICK_EXT_ID, };
// ── Starting deck composition ───────────────────────────────────────────────
// why: Per Legendary board game rules, each player starts with 12 cards:
// 8 S.H.I.E.L.D. Agents (recruit 1) and 4 S.H.I.E.L.D. Troopers (attack 1).
/** Number of S.H.I.E.L.D. Agent cards in each player's starting deck. */
const STARTING_AGENTS_COUNT = 8;
/** Number of S.H.I.E.L.D. Trooper cards in each player's starting deck. */
const STARTING_TROOPERS_COUNT = 4;
// why: D-12903 — HQ slot count is 5 for MVP (graceful extension to 6 reserved
// for set-specific variants). WP-135 reads the same constant when populating
// the HQ from the front of the shuffled hero deck reservoir; recruitHero
// refills exactly one slot per success via refillHqSlot.
/** Number of HQ slots populated from the hero deck at setup time (MVP). */
const HQ_SLOT_COUNT = 5;
/**
 * Builds an unshuffled starting deck of CardExtId strings for one player.
 *
 * @returns Array of 12 CardExtId strings (8 agents + 4 troopers).
 */
function buildStartingDeckCards() {
    const cards = [];
    for (let i = 0; i < STARTING_AGENTS_COUNT; i++) {
        cards.push(SHIELD_AGENT_EXT_ID);
    }
    for (let i = 0; i < STARTING_TROOPERS_COUNT; i++) {
        cards.push(SHIELD_TROOPER_EXT_ID);
    }
    return cards;
}
/**
 * Collapses per-hook Size-Changing grants into a card-keyed lookup for
 * G.cardSizeChangingClasses.
 *
 * Only hooks carrying a non-empty sizeChangingClasses list contribute; a card with no
 * Size-Changing grant gets no entry (the helper treats an absent key as []). Classes are
 * deduplicated per card via a Set — a card that printed and granted the same class, or two
 * Size-Changing lines naming the same class, collapses to one entry.
 *
 * @param hooks - The hero ability hooks built at setup.
 * @returns Per-card granted-class lookup keyed by CardExtId.
 */
// why: D-24074 / WP-290 — the sibling-snapshot build for G.cardSizeChangingClasses,
// mirroring how cardTraits/cardKeywords are resolved once at setup. for...of, no .reduce().
function buildCardSizeChangingClasses(hooks) {
    const grantedByCard = {};
    for (const hook of hooks) {
        if (hook.sizeChangingClasses === undefined || hook.sizeChangingClasses.length === 0) {
            continue;
        }
        const existing = grantedByCard[hook.cardId];
        const classSet = new Set(existing ?? []);
        for (const classSlug of hook.sizeChangingClasses) {
            classSet.add(classSlug);
        }
        grantedByCard[hook.cardId] = [...classSet];
    }
    return grantedByCard;
}
/**
 * Extracts the match selection metadata from a validated config.
 *
 * @param config - Validated match setup config.
 * @returns MatchSelection with the selected entity ext_ids.
 */
function buildMatchSelection(config) {
    return {
        schemeId: config.schemeId,
        mastermindId: config.mastermindId,
        villainGroupIds: [...config.villainGroupIds],
        henchmanGroupIds: [...config.henchmanGroupIds],
        heroDeckIds: [...config.heroDeckIds],
    };
}
/**
 * Extracts scheme card abilities text from the registry at setup time.
 *
 * Walks `registry.getSet(setAbbr).schemes[]` to find the matching slug,
 * then reads `cards[0].abilities[]`. Returns an empty array on any
 * lookup or parse failure — never throws.
 */
function buildSchemeGameText(schemeId, registry) {
    if (!registry || typeof registry !== 'object')
        return [];
    const candidate = registry;
    if (typeof candidate.getSet !== 'function')
        return [];
    const slashIndex = schemeId.indexOf('/');
    if (slashIndex === -1)
        return [];
    const setAbbr = schemeId.slice(0, slashIndex);
    const slug = schemeId.slice(slashIndex + 1);
    if (setAbbr.length === 0 || slug.length === 0)
        return [];
    const getSet = candidate.getSet;
    const setData = getSet(setAbbr);
    if (!setData || typeof setData !== 'object')
        return [];
    const setCandidate = setData;
    if (!Array.isArray(setCandidate.schemes))
        return [];
    for (const entry of setCandidate.schemes) {
        if (!entry || typeof entry !== 'object')
            continue;
        const schemeEntry = entry;
        if (schemeEntry.slug !== slug)
            continue;
        if (!Array.isArray(schemeEntry.cards))
            return [];
        const firstCard = schemeEntry.cards[0];
        if (!firstCard || typeof firstCard !== 'object')
            return [];
        const cardCandidate = firstCard;
        if (!Array.isArray(cardCandidate.abilities))
            return [];
        const gameText = [];
        for (const line of cardCandidate.abilities) {
            if (typeof line === 'string' && line.length > 0) {
                gameText.push(line);
            }
        }
        return gameText;
    }
    return [];
}
// ── Main orchestrator ───────────────────────────────────────────────────────
/**
 * Builds the complete initial LegendaryGameState from a validated config.
 *
 * Delegates player zone construction to buildPlayerState and global pile
 * construction to buildGlobalPiles.
 *
 * @param config - Validated MatchSetupConfig (all 9 fields).
 * @param registry - Card registry for resolving ext_ids. Used by
 *   buildVillainDeck (WP-014B) to resolve villain, henchman, scheme, and
 *   mastermind data at setup time. Satisfies VillainDeckRegistryReader
 *   structurally.
 * @param context - Setup context with ctx.numPlayers and random.Shuffle.
 * @param scoringConfig - Optional setup-time PAR scoring configuration. When
 *   provided, assigned to G.activeScoringConfig to mark the match as
 *   PAR-scored. When undefined, the field stays unset.
 * @returns The fully populated initial LegendaryGameState.
 */
// why: 4th positional optional parameter per D-6703; narrowest additive change
// that keeps the 9-field MatchSetupConfig lock (D-1244) and D-4805
// scenario-config separation intact.
// why: WP-686 / D-24503 — the config param is MatchConfiguration (the payload
// alias = MatchSetupConfig & { finalBlow? }) so this constructor can read the
// optional Final Blow flag. The 9-field composition lock is unchanged;
// MatchConfiguration is assignable to MatchSetupConfig, so buildMatchSelection
// and every other composition reader below are untouched.
export function buildInitialGameState(config, registry, context, scoringConfig) {
    // why: Helpers were extracted from this function to satisfy the 30-line
    // function limit (code-style Rule 5) and to improve testability. Each
    // helper is independently testable with its own shape tests.
    const numPlayers = context.ctx.numPlayers;
    // why: D-10014 — Uniformity Rule + builder signatures don't accept G +
    // remediation pointer to setRegistryForSetup. Per Q3 LOCKED
    // orchestration-only, all four setup-builder diagnostic emissions live
    // here. Each guard returns false on incomplete registry-reader interface
    // (test mocks, server-not-wired). On false, push a full-sentence
    // diagnostic naming (a) which builder was skipped, (b) why, (c) how to
    // fix. Builder-internal `isXRegistryReader → empty` paths remain
    // unchanged for defense-in-depth; this orchestration site is the
    // primary detection seam.
    const setupMessages = [];
    if (!isVillainDeckRegistryReader(registry)) {
        setupMessages.push('buildVillainDeck skipped: the registry-reader interface is incomplete (listCards / listSets / getSet missing or not functions). Verify that setRegistryForSetup(registry) was called at server startup, or that the test mock implements the full reader interface.');
    }
    if (!isMastermindRegistryReader(registry)) {
        setupMessages.push('buildMastermindState skipped: the registry-reader interface is incomplete (listSets / getSet missing or not functions). Verify that setRegistryForSetup(registry) was called at server startup, or that the test mock implements the full reader interface.');
    }
    if (!isSchemeRegistryReader(registry)) {
        setupMessages.push('buildSchemeSetupInstructions skipped: the registry-reader interface is incomplete (listSets / getSet missing or not functions). Verify that setRegistryForSetup(registry) was called at server startup, or that the test mock implements the full reader interface.');
    }
    if (!isHeroAbilityRegistryReader(registry)) {
        setupMessages.push('buildHeroAbilityHooks skipped: the registry-reader interface is incomplete (listCards missing or not a function). Verify that setRegistryForSetup(registry) was called at server startup, or that the test mock implements the full reader interface.');
    }
    // why: WP-111 / EC-118 / D-10014 — orchestration-side detection seam
    // for the new buildCardDisplayData builder. Mirrors the four guards
    // above. Builder-internal `isCardDisplayDataRegistryReader → empty`
    // path remains unchanged for defense-in-depth; this site is the
    // primary detection seam (per the WP-113 D-10014 single-detection-seam
    // pattern). Single full-sentence diagnostic naming (a) which builder
    // skipped, (b) why, (c) how to fix.
    if (!isCardDisplayDataRegistryReader(registry)) {
        setupMessages.push('buildCardDisplayData skipped: the registry-reader interface is incomplete (listCards / getSet missing or not functions). Verify that setRegistryForSetup(registry) was called at server startup, or that the test mock implements the full reader interface.');
    }
    // Build per-player state with shuffled starting decks
    const playerZones = {};
    for (let playerIndex = 0; playerIndex < numPlayers; playerIndex++) {
        const playerId = String(playerIndex);
        const startingDeck = buildStartingDeckCards();
        const playerState = buildPlayerState(playerId, startingDeck, context);
        // why: WP-701 / D-24520 — deal the starting hand at setup. The engine no longer
        // draws at the start of a turn (the draw moved to end-of-turn via
        // applyEndOfTurnCleanup), so every seat's FIRST hand must be dealt here — no
        // onBegin/onEnd runs before seat 0's opening turn. A fresh 12-card deck drawing
        // HAND_SIZE never reshuffles, so this consumes no RNG and the rest of setup's
        // stream (hero deck, Skrull conversion) is byte-unchanged; only the hand/deck
        // split moves. Seat order (playerIndex ascending) is the locked deal order.
        drawCardsIntoHand(playerState.zones, HAND_SIZE, context);
        playerZones[playerId] = playerState.zones;
    }
    // Build global piles sized from config count fields.
    // why: scheme-specific setup sizing (D-24321) — Legacy Virus builds its Wound
    // stack at 6×players (its printed setup), overriding the requested woundsCount.
    // This is a POST-VALIDATION override: the config's woundsCount already passed
    // the 30 floor (D-24032); this sizes the built pile, which for Legacy Virus is
    // deliberately below the floor. Other schemes pass through unchanged.
    const effectiveWoundsCount = resolveEffectiveWoundsCount(config.schemeId, numPlayers, config.woundsCount);
    const piles = buildGlobalPiles({ ...config, woundsCount: effectiveWoundsCount }, context);
    // Build selection metadata from config
    const selection = buildMatchSelection(config);
    // Build villain deck from registry data
    // why: villain deck built from registry data at setup time; see D-1410
    // through D-1413 for ext_id conventions and composition rules. The real
    // CardRegistry satisfies VillainDeckRegistryReader structurally. Test mocks
    // that only implement CardRegistryReader (listCards with {key} only) will
    // lack listSets/getSet — buildVillainDeck handles this gracefully by
    // producing an empty deck, which the reveal pipeline already supports.
    // why: D-24736 — Clash of the Monsters Unleashed shuffles the 8 Monsters Unleashed
    // Villains into a face-down Monster Pit. villainBuildConfig adds that group so every
    // per-card villain builder below (deck, cardStats, keywords, traits, defeat
    // requirements, Blood Frenzy, VP, display data, ability hooks) registers the Monsters;
    // splitMonsterPitFromVillainDeck then lifts them out of the shuffled deck. For every
    // other scheme villainBuildConfig carries the same ids, so their setup is unchanged.
    // G.selection / matchConfiguration keep the loadout's own ids.
    const villainBuildConfig = {
        ...config,
        villainGroupIds: resolveEffectiveVillainGroupIds(config.schemeId, config.villainGroupIds),
    };
    const villainDeckResult = buildVillainDeck(villainBuildConfig, registry, context);
    // why: WP-823 / D-24756 — Brainwash the Military's 12 Officers come from the
    // S.H.I.E.L.D. Officer Stack, so the Stack starts that many cards smaller. The
    // tokens are identical and the Stack's shuffle already ran, so this slice draws
    // nothing at random. 0 (no write) for every other scheme.
    const brainwashedOfficerCount = countBrainwashedOfficers(villainDeckResult.convertedOrigins);
    if (brainwashedOfficerCount > 0) {
        piles.officers = piles.officers.slice(brainwashedOfficerCount);
    }
    // why: WP-513 — the Killbots "twists next to this Scheme" counter (D-24325) is
    // seeded at 3 (the card's "3 additional Twists next to this Scheme" placed at
    // setup) and drives every Killbot's attack. hasKillbots is decided by
    // buildVillainDeck (bystander conversion). Secret Invasion's skrull conversion
    // (WP-514 / D-24326) runs later — after the hero deck is built — and augments the
    // converted-villain overlay + the villain deck; the final convertedVillainOrigins
    // and its hasConvertedVillains flag (RS-1 lazy materialization: the overlay is
    // included in G only for a converting scheme, so non-converting games omit the
    // key and their state hash is unchanged) are computed there.
    const hasKillbots = Object.values(villainDeckResult.convertedOrigins).includes('killbot');
    const initialCounters = hasKillbots
        ? { [KILLBOT_TWISTS_NEXT_TO_SCHEME]: 3 }
        : {};
    // why: cardStats extracted to local variable so buildMastermindState
    // can add the mastermind base card entry to it. buildMastermindState
    // MUST execute after buildCardStats (ordering invariant — EC-019).
    const cardStats = buildCardStats(registry, villainBuildConfig);
    // why: card keywords resolved at setup from registry so moves never query
    // registry at runtime — same pattern as G.cardStats (WP-018) and
    // G.villainDeckCardTypes (WP-014B).
    const cardKeywords = buildCardKeywords(registry, villainBuildConfig);
    // why: WP-179 — categorical traits (heroClass, team) resolved at setup from
    // registry. Same sibling-snapshot pattern as cardStats/cardKeywords/cardDisplayData.
    const cardTraits = buildCardTraits(registry, villainBuildConfig);
    // why: D-24074 / WP-290 — hero ability hooks are hoisted to a local so the
    // Size-Changing granted-class snapshot can be derived from them (the parser parked the
    // grant on hook.sizeChangingClasses). Built once at setup, immutable during gameplay.
    const heroAbilityHooks = buildHeroAbilityHooks(registry, config);
    // why: D-24074 / WP-290 — collapse the per-hook Size-Changing grants into a card-keyed
    // snapshot (sibling to cardTraits). The grant is an additive second class source read by
    // the sizeChanging.logic.ts helper at class-read time; cardTraits is never mutated.
    const cardSizeChangingClasses = buildCardSizeChangingClasses(heroAbilityHooks);
    // why: WP-292 / D-24076 — per-villain defeat requirements built from registry at
    // setup, keyed by copy-indexed instance ext_id. A fight precondition read by the
    // fightVillain gate; sibling-snapshot pattern, immutable during gameplay. Narrow
    // test mocks → empty record (conditional-spread below keeps the G shape stable).
    const villainDefeatRequirements = buildVillainDefeatRequirements(registry, villainBuildConfig);
    // why: WP-817 / D-24750 — per-villain fight discard costs (Primus), the sibling
    // table of villainDefeatRequirements, built from the same registry read. Narrow
    // test mocks → empty record (omitted below).
    const villainFightDiscardCosts = buildVillainFightDiscardCosts(registry, villainBuildConfig);
    // why: WP-841 / D-24774 — per-villain variable-Attack passives (Baron Strucker,
    // Paibok, Super-Skrull, Hydro-Man), built from the same registry read and read only
    // by resolveFightCost. Narrow test mocks → empty record (omitted below).
    const villainVariableAttack = buildVillainVariableAttack(registry, villainBuildConfig);
    // why: WP-760 / D-24589 — Blood Frenzy villain instances (Metarchus, Salomé),
    // scanned once at setup from the selected villain groups and read only by
    // resolveFightCost. Narrow test mocks → empty record (omitted below).
    const villainBloodFrenzy = buildVillainBloodFrenzy(registry, villainBuildConfig);
    // why: scheme setup runs after base construction, before first turn.
    // Instructions configure the board (counters, keywords, city state).
    // Separate from scheme twist execution (WP-024).
    const schemeSetupInstructions = buildSchemeSetupInstructions(config.schemeId, registry);
    // why: mastermind state built from registry at setup time; base card
    // fightCost added to cardStats so fightMastermind reads it without
    // registry access. Narrow test mocks produce empty state gracefully.
    const mastermindState = buildMastermindState(config.mastermindId, registry, context, cardStats);
    // why: WP-815 / D-24747 — Abomination kinds for the selected villain copies and the
    // mastermind base face, read only by the two fight-cost resolvers. Placed after
    // buildMastermindState because it keys the mastermind by the baseCardId that call
    // just computed; villainBuildConfig carries the scheme-effective villain groups (the
    // Blood Frenzy precedent). Narrow test mocks → empty record (omitted below).
    const cardAbomination = buildCardAbomination(registry, villainBuildConfig, config.mastermindId, mastermindState.baseCardId);
    // why: D-24157 — printed victory points for VP-bearing cards (villains,
    // henchmen, the mastermind) resolved at setup so computeFinalScores awards
    // printed VP without runtime registry access — sibling snapshot to cardStats.
    // Placed after buildMastermindState because it keys the mastermind vp by the
    // baseCardId that call just computed.
    const cardVictoryPoints = buildCardVictoryPoints(registry, villainBuildConfig, mastermindState.baseCardId);
    // why: WP-111 / EC-118 — sibling-snapshot to G.cardStats / G.cardKeywords /
    // G.villainDeckCardTypes. Surfaces card display fields (name, imageUrl,
    // cost) into UIState so arena-client renders real cards instead of
    // CardExtId strings — without granting the client a runtime registry
    // import. Placed after buildMastermindState because the completeness
    // sweep below uses cardStats (which now contains the mastermind base
    // card entry) as the expected-key set.
    // why: WP-172 — `numPlayers` (3rd arg) is the bystander-count fallback
    // source for section 7 of `buildCardDisplayData` per D-1412; mirrors
    // `villainDeck.setup.ts:262` read of `context.ctx.numPlayers`. Do NOT
    // substitute `config.bystandersCount` (that sizes the rescue-pile
    // supply in `G.sharedPiles.bystanders`, a different domain — see the
    // `numPlayers` @param JSDoc in `buildCardDisplayData.ts`).
    const cardDisplayData = buildCardDisplayData(registry, villainBuildConfig, numPlayers);
    // why: D-24668 — Killbot-converted villain-deck Bystanders display as "Killbot", so the
    // log, City card and Victory Pile name them as the Villains they are. A no-op for every
    // scheme without Killbots.
    applyKillbotDisplayNames(cardDisplayData, villainDeckResult.convertedOrigins);
    // why: WP-823 / D-24756 — each Brainwash the Military Officer displays as a
    // S.H.I.E.L.D. Officer (name and art). A no-op for every scheme without Officers.
    applyBrainwashedOfficerDisplay(cardDisplayData, villainDeckResult.convertedOrigins);
    // why: WP-111 / EC-118 / PS-8 — setup-time diagnostic surface for missing
    // display entries. Preserves WP-028 D-2801 projection-purity contract:
    // buildUIState MUST NOT mutate G.messages. The diagnostic surface lives
    // here at setup time, mirroring the WP-113 D-10014 single-detection-seam
    // pattern. One consolidated diagnostic per setup, never per-card.
    // Projection-time placeholder fallback (UNKNOWN_DISPLAY_PLACEHOLDER) is
    // a pure render path with no G interaction.
    const completenessMessage = auditCardDisplayDataCompleteness(cardStats, cardDisplayData);
    if (completenessMessage !== null) {
        setupMessages.push(completenessMessage);
    }
    // why: starting cards are not set-specific registry entries — they are
    // well-known game components absent from buildCardStats' registry walk.
    // Without explicit entries, playCard falls back to 0/0 attack/recruit.
    // Per tabletop rules: Agents give 1 recruit, Troopers give 1 attack.
    // Placed after the completeness audit so the audit only checks
    // registry-derived cards (starting cards have no cardDisplayData entry).
    // why: WP-675 / D-24490 — synthesized token icon presence is set from the real
    // value (no raw "0+" ambiguity): the Agent shows a recruit icon, the Trooper an attack icon.
    // why: WP-677 / D-24493 — the three basic S.H.I.E.L.D. cards (Agent/Trooper/Officer)
    // are S.H.I.E.L.D. cards by name, so they count toward S.H.I.E.L.D. Level; the Sidekick
    // is not.
    cardStats[SHIELD_AGENT_EXT_ID] = { attack: 0, recruit: 1, cost: 0, fightCost: 0, fightCostMode: 'static', fightCostBase: 0, hasAttackIcon: false, hasRecruitIcon: true, isShieldOrHydra: true };
    cardStats[SHIELD_TROOPER_EXT_ID] = { attack: 1, recruit: 0, cost: 0, fightCost: 0, fightCostMode: 'static', fightCostBase: 0, hasAttackIcon: true, hasRecruitIcon: false, isShieldOrHydra: true };
    // why: supply-pile tokens (Officer, Sidekick) are likewise well-known
    // components absent from the registry walk. Per the tabletop rulebook,
    // a S.H.I.E.L.D. Officer recruits for 3 and plays for +2 recruit; a
    // Sidekick recruits for 2 (once per turn) and its printed value is a
    // draw-two ability rather than face stats, so attack/recruit stay 0.
    // No engine path moves these out of G.piles yet, but every cost read
    // falls back to 0 on a missing entry (the `?? 0` convention shared by
    // recruitHero and the 0-cost eligibility predicates), which would make
    // both tokens free recruits and false 0-cost matches the moment a
    // gain/recruit-from-pile effect ships.
    // why: WP-675 / D-24490 — the Officer shows a +2 recruit icon; the Sidekick's printed
    // value is a draw-two ability, not a face attack/recruit icon (both false).
    cardStats[SHIELD_OFFICER_EXT_ID] = { attack: 0, recruit: 2, cost: 3, fightCost: 0, fightCostMode: 'static', fightCostBase: 0, hasAttackIcon: false, hasRecruitIcon: true, isShieldOrHydra: true };
    cardStats[SIDEKICK_EXT_ID] = { attack: 0, recruit: 0, cost: 2, fightCost: 0, fightCostMode: 'static', fightCostBase: 0, hasAttackIcon: false, hasRecruitIcon: false, isShieldOrHydra: false };
    // why: WP-135 — build the per-match hero deck reservoir from
    // MatchSetupConfig.heroDeckIds via the locked rarity → copy-count map
    // (D-13501; 5/3/3/3 = 14 cards per hero across the four-label set).
    // Single ctx.random.Shuffle call inside buildHeroDeck — the determinism
    // envelope is locked here; no per-turn reshuffle. fillHqFromDeck takes
    // the first 5 cards into G.hq slots 0..4 (deck top → slot 0); the
    // remainder lives at G.heroDeck. Narrow test mocks → empty reservoir →
    // empty HQ (mirrors sibling builders).
    // why: WP-515 / D-24328 — Super Hero Civil War's printed setup sizes the Hero Deck to
    // 4 groups at exactly 2 players; resolveEffectiveHeroDeckIds applies that POST-VALIDATION
    // override on the id list fed to the existing single shuffle (the loadout still validated
    // its normal 5 ids). This makes WP-510/D-24318's hero-deck-depletion loss reachable at 2p
    // (the 5-group deck was too big to run out). Every other scheme / player count is unchanged
    // → byte-identical. Runs BEFORE the WP-514 convertHeroesToSkrulls step (a passthrough for
    // Civil War), so the two sizing paths never overlap.
    const effectiveHeroDeckIds = resolveEffectiveHeroDeckIds(config.schemeId, numPlayers, [...config.heroDeckIds]);
    const shuffledHeroDeck = buildHeroDeck(effectiveHeroDeckIds, registry, context);
    // why: D-24468 (WP-657) — the transform side deck is the complementary
    // partition of the SAME effective hero set: buildHeroDeck returned the
    // shuffled reservoir WITHOUT transform cards, buildTransformSideDeck returns
    // the transform cards held back. Unshuffled (no ctx.random) so it adds no
    // draw and leaves the Shuffle envelope — and thus every non-transform game's
    // state hash — byte-identical. Runs off effectiveHeroDeckIds so a Civil War
    // 4-group override (WP-515) sets aside transform cards for exactly the heroes
    // in play. Transform cards are absent from shuffledHeroDeck, so the WP-514
    // Skrull conversion above never draws them (a side-deck card is not in the
    // Hero Deck to convert — correct by the tabletop rule).
    const transformSideDeck = buildTransformSideDeck(effectiveHeroDeckIds, registry);
    // why: D-24469 (WP-658) — capture the base→second-form link the
    // [keyword:Transform] runtime needs at play time, off the SAME effective hero
    // set as the side deck above. Moves have no registry, so heroEffectTransform
    // reads this G-resident map (built from each base card's `transform` field)
    // instead of the registry. A pure registry walk (no ctx.random) — like the
    // side deck it adds no draw and leaves the Shuffle envelope untouched. Empty
    // {} for non-transform hero sets, but ALWAYS seeded (mirrors transformDeck),
    // so a new top-level G field re-pins the two hash oracles (D-24468 precedent).
    const transformTargets = buildTransformTargets(effectiveHeroDeckIds, registry);
    // why: WP-724 / D-24545 — capture the split-hero primary→alternate face link the
    // "choose a side" runtime (resolveSplitFaceChoice) needs at play time, off the SAME
    // effective hero set. Moves have no registry, so buildSplitFaces resolves it once here.
    // Pure registry walk (no ctx.random). Empty {} for non-split hero sets (incl. the core
    // sentinel); the conditional spread below keeps the field ABSENT when empty so a no-split
    // game serializes byte-identically → NO hash re-pin (the schemeTransformFields precedent;
    // contrast transformTargets, which is always-seeded and DID re-pin).
    const splitFaces = buildSplitFaces(effectiveHeroDeckIds, registry);
    const splitFacesFields = Object.keys(splitFaces).length > 0 ? { splitFaces } : {};
    // why: display-only printed-order capture for the "choose a side" picker (sides[] is not
    // left-to-right; lower slot = left half). Same absent-when-empty spread, so games whose split
    // cards are all primary-on-left (and every no-split game) serialize byte-identically.
    const splitFacesAlternateOnLeft = buildSplitFaceAlternateOnLeft(effectiveHeroDeckIds, registry);
    const splitFacesAlternateOnLeftFields = Object.keys(splitFacesAlternateOnLeft).length > 0 ? { splitFacesAlternateOnLeft } : {};
    // why: WP-670 / D-24484 — Scheme Transform. For a scheme in SCHEME_TRANSFORM_TARGETS,
    // capture its Great Old One flip target + that face's ability text (read the same way as
    // the base scheme's gameText) so the runtime flip is a pure state read. A conditional
    // spread below keeps the three fields ABSENT for every other scheme, so a non-transform
    // game (incl. the sentinel core/legacy-virus-the) serializes byte-identically → no re-pin.
    const schemeTransformTarget = SCHEME_TRANSFORM_TARGETS[config.schemeId];
    const schemeTransformFields = schemeTransformTarget !== undefined
        ? {
            transformTargetSchemeId: schemeTransformTarget.targetSchemeId,
            transformTargetGameText: buildSchemeGameText(schemeTransformTarget.targetSchemeId, registry),
            hasTransformed: false,
        }
        : {};
    // why: WP-514 / D-24326 — Secret Invasion converts 12 Heroes from the reservoir
    // into Skrull Villains and shuffles them into the Villain Deck. The 12 are drawn
    // from the top of the shuffled reservoir BEFORE fillHqFromDeck consumes it (so they
    // never also land in the HQ / G.heroDeck — RS-4), and the villain deck is re-shuffled
    // by a SINGLE new ctx.random draw. That draw runs AFTER the hero-deck shuffle (the
    // prior last random draw) so it is the LAST draw in setup; non-Secret-Invasion games
    // take the passthrough (no new draw, no overlay), leaving their state hash
    // byte-identical.
    const skrullConversion = convertHeroesToSkrulls(config.schemeId, shuffledHeroDeck, villainDeckResult.state, villainDeckResult.cardTypes, villainDeckResult.convertedOrigins, context);
    const convertedVillainOrigins = skrullConversion.convertedOrigins;
    // why: D-24736 — lift the Monster Pit out of the shuffled villain deck (Clash only;
    // a passthrough with monsterPit undefined for every other scheme). Keeping the
    // Monsters' relative deck order gives a shuffled Pit with no extra random draw.
    const monsterPitSplit = splitMonsterPitFromVillainDeck(config.schemeId, skrullConversion.villainDeckState);
    const hasConvertedVillains = Object.keys(convertedVillainOrigins).length > 0;
    const filledHqResult = fillHqFromDeck(skrullConversion.heroReservoir, HQ_SLOT_COUNT);
    // why: WP-562 / D-24371 §2 — capture the depletion-loss pile's setup size so the
    // meter can measure the scheme's OWN Evil-Wins condition. Written LAZILY: the
    // resolver returns undefined for every scheme that does not declare a
    // 'pile-depleted' condition, and the key is then omitted below, so those games'
    // state hashes are unchanged. The sentinel fixture is NOT one of them — it uses
    // core/legacy-virus-the, which IS pile-depleted, so this field is the cause of
    // that fixture's expected finalStateHash re-pin. PRE_WP080_HASH replays an empty
    // state with no scheme and must stay unchanged.
    // why: the hero-deck size read here is shuffledHeroDeck.length — the TOTAL hero
    // cards BUILT (42 in a 3-hero core game), NOT filledHqResult.remainingDeck.length
    // (37, after 5 go to the HQ). Operator decision 2026-08-17, overruling the
    // drafting recommendation of 37: 42 is the number a player counts on the table,
    // and the 5 HQ cards are recruitable rather than gone. Do not "correct" this to 37.
    const schemeLossPileSetupSize = resolveSchemeLossPileSetupSize(config.schemeId, shuffledHeroDeck.length, piles.wounds.length);
    // why: WP-763 / D-24595 — the Villain Deck gets its OWN lazy field rather than
    // reusing schemeLossPileSetupSize, so the sentinel (core/legacy-virus-the) hash
    // that depends on that field is untouched, and a "Hero Deck or Villain Deck"
    // scheme can measure both piles. Written only when the scheme's condition names
    // 'villainDeck'. Sized from the built deck (after any Skrull conversion) — never
    // from the villainDeckCardTypes key count, which a Secret Invasion twist mutates.
    const schemeLossVillainDeckSetupSize = resolveSchemeLossVillainDeckSetupSize(config.schemeId, monsterPitSplit.villainDeckState.deck.length);
    // why: build the base state first, then apply scheme setup instructions.
    // executeSchemeSetup returns updated state — pure function, no mutation.
    // At MVP, schemeSetupInstructions is always [], so this is a no-op passthrough.
    const baseState = {
        matchConfiguration: config,
        // why: WP-686 / D-24503 — seed the Final Blow flag ONLY when the payload
        // sets it true; omit it otherwise. An unconditional `finalBlow: config.finalBlow`
        // would write `finalBlow: false`/`undefined` into every match's state and,
        // because both whole-G hash oracles serialize the entire G, move
        // PRE_WP080_HASH + finalStateHash for every match. Omit-when-off keeps a
        // non–Final-Blow match byte-identical (the schemeLossPileSetupSize /
        // activeScoringConfig precedent below). Read by WP-687 as `finalBlow === true`.
        ...(config.finalBlow === true ? { finalBlow: true } : {}),
        // why: WP-802 / D-24689 — seed the operator-match flag ONLY for a strict
        // `true` and omit it otherwise. Writing `isDebugMatch: false` would move
        // PRE_WP080_HASH and every finalStateHash; omit-when-off keeps every normal
        // match byte-identical (the WP-686 finalBlow precedent directly above).
        ...(config.isDebugMatch === true ? { isDebugMatch: true } : {}),
        // why: WP-805 / D-24700 — seed the random-first-player flag ONLY for two or more
        // seats and omit it for solo. Omit-when-off keeps every solo match and solo hash
        // oracle byte-identical (the finalBlow / isDebugMatch precedent directly above);
        // a flagless G is how the play phase recognizes a pre-WP-805 match.
        ...(context.ctx.numPlayers > 1 ? { isFirstPlayerRandom: true } : {}),
        selection,
        // why: currentStage is initialized to the first canonical turn stage.
        // The play phase onBegin hook resets it on each new turn. During setup
        // and lobby phases, currentStage is not meaningful but must be present
        // because LegendaryGameState requires it for JSON-serializability.
        // why: TURN_STAGES is a readonly array with known contents. The non-null
        // assertion is safe because TURN_STAGES always has at least one element
        // (enforced by drift-detection tests in WP-007A).
        currentStage: TURN_STAGES[0],
        // why: the start-of-turn villain reveal has not occurred at setup time; the
        // play phase onBegin hook resets this to false on every turn including turn 1.
        villainRevealedThisTurn: false,
        // why: WP-701 / D-24520 — setup DEALS every player's starting hand (above), so
        // the turn-1 hand is already drawn; the flag is TRUE so the scaffold drawCards
        // move is a guarded no-op from the opening turn (onBegin keeps it true).
        hasDrawnThisTurn: true,
        // why: WP-379 — neither turn-action flag is set at setup time; the play phase
        // onBegin hook resets both to false on every turn. hasActedThisTurn gates the
        // Wound Healing ability; hasHealedThisTurn reverse-locks fight/recruit
        // (D-24179 / D-24180).
        hasActedThisTurn: false,
        hasHealedThisTurn: false,
        playerZones,
        piles,
        // why: WP-434 — setup-time diagnostics are plain narration; wrap each as a
        // neutral LogEntry so G.messages is LogEntry[] from initialization. logMeta is
        // not yet stamped at setup, so these carry no numbering prefix (matches prior
        // behaviour where setup messages were bare strings).
        messages: setupMessages.map((text) => ({ text, outcome: 'neutral' })),
        counters: initialCounters,
        hookRegistry: buildDefaultHookDefinitions(config),
        // why: villain deck built from registry data at setup time; see D-1410
        // through D-1413 for ext_id conventions and composition rules. WP-514: for
        // Secret Invasion the deck + card-type map are the skrull-augmented versions
        // (12 Heroes injected as 'villain'-typed Skrulls); passthrough for every other
        // scheme (byte-identical to villainDeckResult.state / .cardTypes).
        villainDeck: monsterPitSplit.villainDeckState,
        villainDeckCardTypes: skrullConversion.villainDeckCardTypes,
        // why: D-24736 — omit-when-absent: only a Clash game carries a Monster Pit, so
        // every other game's state hash is unchanged (the convertedVillainOrigins pattern).
        ...(monsterPitSplit.monsterPit !== undefined ? { monsterPit: monsterPitSplit.monsterPit } : {}),
        // why: WP-513 / D-24324 + WP-514 / D-24326 — lazy: include the converted-villain
        // overlay ONLY when a scheme converted cards (Killbots or Secret Invasion), so
        // non-converting games omit the key entirely and their state hash is unchanged (RS-1).
        ...(hasConvertedVillains ? { convertedVillainOrigins } : {}),
        // why: WP-562 / D-24371 §2 — spread-in ONLY when the scheme declares a
        // 'pile-depleted' loss, mirroring the convertedVillainOrigins lazy pattern
        // above. An unconditional `schemeLossPileSetupSize: undefined` would still
        // serialize a key into every game's state and move PRE_WP080_HASH, which the
        // packet treats as a STOP condition rather than a re-pin.
        ...(schemeLossPileSetupSize !== undefined ? { schemeLossPileSetupSize } : {}),
        // why: WP-763 / D-24595 — omit-when-absent, the same lazy pattern, so every
        // scheme whose condition does not name the Villain Deck (all 8 core schemes)
        // stays byte-identical.
        ...(schemeLossVillainDeckSetupSize !== undefined
            ? { schemeLossVillainDeckSetupSize }
            : {}),
        // why: KO pile starts empty; cards enter via koCard helper (WP-017)
        ko: [],
        // why: no bystanders attached at game start; populated during reveals (WP-017)
        attachedBystanders: {},
        // why: no heroes captured at game start; populated by captureHqHero* effects (WP-214)
        villainAttachedHeroes: {},
        // why: City initialized empty; villains enter via revealVillainCard (WP-015)
        city: initializeCity(),
        // why: HQ filled from first 5 of the shuffled hero deck via
        // fillHqFromDeck; remainder stored at G.heroDeck per WP-135.
        hq: filledHqResult.hq,
        // why: G.heroDeck holds the post-shuffle, post-HQ-fill remainder of
        // the per-match hero deck reservoir. Single source for refilling
        // vacated HQ slots inside recruitHero (FIFO via refillHqSlot;
        // empty-deck branch leaves the slot null per D-13503).
        heroDeck: filledHqResult.remainingDeck,
        // why: D-24468 (WP-657) — Transform second-form cards set aside out of the
        // shuffled reservoir into a face-up side deck. Empty for hero sets without
        // transform cards (only wwhk heroes today). Read-only in WP-657; the
        // [keyword:Transform] runtime that consumes it is a named follow-up WP.
        transformDeck: transformSideDeck,
        // why: D-24469 (WP-658) — the base→second-form card-key map the
        // [keyword:Transform] runtime reads at play time. Built from each base
        // card's `transform` field; empty {} for non-wwhk games. Always present
        // (mirrors transformDeck), so the two engine hash oracles re-pin.
        transformTargets,
        // why: WP-724 / D-24545 — split-hero primary→alternate face-key map, present ONLY when
        // the loadout has split heroes (conditional spread), so a no-split game (incl. the
        // sentinel) serializes byte-identically → no re-pin.
        ...splitFacesFields,
        ...splitFacesAlternateOnLeftFields,
        // why: mastermind state built at setup from registry; tactics deck
        // shuffled deterministically; base card fightCost in G.cardStats
        mastermind: mastermindState,
        // why: scheme runtime state holds the twist pile for resolved
        // scheme-twist cards. Separate from schemeSetupInstructions (D-2601).
        // gameText: abilities from the registry so UIState can tell the player
        // what the scheme does and what happens on a Scheme Twist.
        scheme: { twistPile: [], gameText: buildSchemeGameText(config.schemeId, registry), ...schemeTransformFields },
        // why: escaped pile is top-level because CityZone is a fixed 5-tuple
        // that cannot host named fields. Append-only, chronological order.
        escapedPile: [],
        // why: card stats resolved at setup from registry so moves never query
        // registry at runtime — same pattern as G.villainDeckCardTypes (WP-014).
        // Read-only after setup (mastermind base card added by buildMastermindState).
        cardStats,
        // why: WP-179 — categorical traits built from registry at setup time.
        // Sibling-snapshot pattern matching cardStats/cardKeywords/cardDisplayData.
        cardTraits,
        // why: board keywords resolved at setup from registry — same pattern as
        // cardStats and villainDeckCardTypes. Immutable during gameplay.
        cardKeywords,
        // why: WP-111 / EC-118 — sibling-snapshot for UI display data
        // (name / imageUrl / cost). Built once at setup; read only by
        // uiState.build.ts. Gameplay reads G.cardStats — never
        // G.cardDisplayData (presentation-vs-gameplay separation lock).
        cardDisplayData,
        // why: scheme setup instructions stored for replay observability (D-2601).
        // Empty at MVP — no structured scheme metadata in registry yet.
        schemeSetupInstructions,
        // why: economy starts at zero; reset again at each turn start
        turnEconomy: resetTurnEconomy(),
        // why: hero ability hooks built from registry at setup time — same
        // pattern as hookRegistry and cardStats. Immutable during gameplay.
        // Execution deferred to WP-022+. Hoisted above so the Size-Changing
        // granted-class snapshot can be derived from the same hooks (D-24074).
        heroAbilityHooks,
        // why: villain/henchman ability hooks built from registry at setup — same
        // pattern as heroAbilityHooks. Immutable during gameplay; executed at the
        // Fight/Ambush fire sites (WP-185). Narrow test mocks → empty table.
        villainAbilityHooks: buildVillainAbilityHooks(registry, villainBuildConfig),
        // why: WP-200 — append-only structured event log initialised empty.
        // Populated at runtime by the four fire-site emissions
        // (fightVillain.ts / villainDeck.reveal.ts ambush branch /
        // schemeTwistResolvers.ts × 5 / mastermindHandlers.ts). Replay
        // determinism: identical setup + identical moves produces a
        // byte-identical event sequence.
        notableEvents: [],
        // why: lobby state initialized at setup time from ctx.numPlayers. All
        // players start as not ready. G.lobby.started is false until
        // startMatchIfReady succeeds.
        lobby: {
            requiredPlayers: context.ctx.numPlayers,
            ready: {},
            started: false,
        },
        // why: WP-257 / D-24034 — the hollow-effect diagnostics channel (G.diagnostics)
        // resets EMPTY at this match-creation boundary. "Empty" is the ABSENT optional
        // field (lazy-init at the writer recordHollowEffect, mirroring
        // pendingOptionalKoRewards), deliberately NOT a seeded
        // `{ hollowEffects: [], hollowEffectsDropped: 0 }` literal: a fresh match starts
        // with zero records either way, but an always-present empty literal would change
        // the whole-G canonical-JSON hash. NOTE (WP-451 / D-24271 → WP-488 / D-24294): BOTH
        // hash oracles now EXCLUDE `diagnostics` — the fixture-golden `hashGameState` oracle
        // (WP-451) and, since WP-488, `computeStateHash` (replay.hash.ts, the whole-G
        // determinism / PRE_WP080 oracle) too. So the absent-on-fresh choice is no longer
        // load-bearing for hash stability; it is kept as the correct lazy-init discipline —
        // `G.diagnostics` materializes at its writer (recordHollowEffect / recordEffectTrace),
        // never seeded here, so a fresh match has `G.diagnostics === undefined` (≡ empty,
        // omitted by JSON.stringify) and the first hollow record OR effect trace materializes
        // the channel. There is no mid-match reset — each match calls buildInitialGameState
        // fresh. Hence the field is intentionally NOT assigned in this literal.
        // why: conditional spread per WP-029 exactOptionalPropertyTypes pattern —
        // the field is included only when scoringConfig was supplied; never written
        // as `activeScoringConfig: undefined` literally (D-6703).
        ...(scoringConfig !== undefined ? { activeScoringConfig: scoringConfig } : {}),
        // why: D-24074 / WP-290 — conditional spread, same hash-stability discipline as
        // G.diagnostics / activeScoringConfig above: include cardSizeChangingClasses ONLY when
        // some selected card grants a class. hashGameState serializes the whole G, so an
        // always-present empty `{}` would shift the finalStateHash of every game — including
        // fixtures with no Size-Changing hero, which AC-10 forbids (a shape-only shift is not a
        // grant-attributable class-condition change). Absent ≡ empty (getGrantedClasses returns
        // [] either way), so games without a Size-Changing card stay byte-identical.
        ...(Object.keys(cardSizeChangingClasses).length > 0 ? { cardSizeChangingClasses } : {}),
        // why: D-24157 / WP-365 — conditional spread, same hash-stability discipline as
        // cardSizeChangingClasses above: include cardVictoryPoints ONLY when some VP-bearing
        // card carries a printed vp. hashGameState serializes the whole G, so an always-present
        // empty `{}` would shift the finalStateHash of every game — including the EMPTY_REGISTRY
        // replay fixtures (which resolve no vp), which would force a needless sentinel re-pin.
        // Absent ≡ empty (scoring's `?? VP_<category>` fallback applies either way), so games
        // with no printed vp stay byte-identical.
        ...(Object.keys(cardVictoryPoints).length > 0 ? { cardVictoryPoints } : {}),
        // why: WP-292 / D-24076 — conditional spread, same hash-stability discipline as
        // cardSizeChangingClasses above: include villainDefeatRequirements ONLY when a
        // selected villain carries a [require-to-defeat:...] marker. Absent ≡ empty (the
        // gate treats a missing entry as "no requirement"), so matches without Blob /
        // Venom / Zombie Venom keep a byte-identical finalStateHash.
        ...(Object.keys(villainDefeatRequirements).length > 0 ? { villainDefeatRequirements } : {}),
        // why: WP-817 / D-24750 — same hash-stability discipline: villainFightDiscardCosts is
        // on G ONLY when a selected villain carries a [discard-to-fight:...] marker, so every
        // match without Primus keeps a byte-identical finalStateHash.
        ...(Object.keys(villainFightDiscardCosts).length > 0 ? { villainFightDiscardCosts } : {}),
        // why: WP-841 / D-24774 — same hash-stability discipline: villainVariableAttack is
        // on G ONLY when a selected villain carries a [variable-attack:...] marker, so every
        // match without those four co2e villains keeps a byte-identical finalStateHash.
        ...(Object.keys(villainVariableAttack).length > 0 ? { villainVariableAttack } : {}),
        // why: WP-760 / D-24589 — same hash-stability discipline: villainBloodFrenzy is on G
        // ONLY when a selected villain group prints Blood Frenzy, so every other match keeps
        // a byte-identical finalStateHash.
        ...(Object.keys(villainBloodFrenzy).length > 0 ? { villainBloodFrenzy } : {}),
        // why: WP-815 / D-24747 — same hash-stability discipline (the villainBloodFrenzy
        // precedent): cardAbomination is on G ONLY when a selected villain or the mastermind
        // prints Abomination, so every other match keeps a byte-identical finalStateHash.
        ...(Object.keys(cardAbomination).length > 0 ? { cardAbomination } : {}),
    };
    return executeSchemeSetup(baseState, schemeSetupInstructions);
}
// ---------------------------------------------------------------------------
// auditCardDisplayDataCompleteness — pure helper for PS-8 diagnostic
// ---------------------------------------------------------------------------
/**
 * Audits whether every CardExtId expected by gameplay has a matching
 * display entry. Returns a single consolidated diagnostic string when
 * any expected key is missing; returns null when every key is covered.
 *
 * Expected-key set is taken from `cardStats` because both builders
 * iterate the same four card-type surfaces (heroes / villains /
 * henchmen / mastermind base card) and use the same CardExtId join key
 * conventions. After buildMastermindState runs, cardStats contains
 * every gameplay-relevant ext_id; cardDisplayData should contain a
 * superset (display data for the same set, no extra entries).
 *
 * The completeness sweep is the PS-8 setup-time diagnostic surface that
 * preserves WP-028 D-2801 projection-purity (buildUIState never mutates
 * G.messages). One consolidated diagnostic per setup; never per-card.
 *
 * @param cardStats - Populated card stats record (gameplay source of
 *   truth for which ext_ids should exist).
 * @param cardDisplayData - Populated card display data record.
 * @returns Single consolidated diagnostic string, or null when every
 *   expected key is present.
 */
// why: WP-113 D-10014 single-detection-seam pattern; aggregate the
// missing list into one message rather than emitting per-card noise.
function auditCardDisplayDataCompleteness(cardStats, cardDisplayData) {
    const missing = [];
    for (const expectedKey of Object.keys(cardStats)) {
        if (cardDisplayData[expectedKey] === undefined) {
            missing.push(expectedKey);
        }
    }
    if (missing.length === 0)
        return null;
    const plural = missing.length === 1 ? 'entry' : 'entries';
    return `buildCardDisplayData under-emitted: ${String(missing.length)} expected display ${plural} missing. Missing extIds: ${missing.join(', ')}. Fix: verify the registry-reader interface (listCards / getSet) is fully wired via setRegistryForSetup(registry), and that the test mock implements both methods.`;
}
//# sourceMappingURL=buildInitialGameState.js.map